import { useCallback, useEffect, useState } from 'react';
import {
  getNotifications,
  markNotificationAsRead as markReadService,
  markAllNotificationsAsRead as markAllReadService,
  deleteNotification as deleteService,
  type MockNotification,
} from './mockNotificationsService';

const DEFAULT_PAGE_SIZE = 6;

interface UseMockNotificationsOptions {
  limit?: number;
}

export interface UseMockNotificationsResult {
  notifications: MockNotification[];
  unreadCount: number;
  totalCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  markAsRead: (notificationId: number) => void;
  markAllAsRead: () => void;
  remove: (notificationId: number) => void;
}

export function useMockNotifications(
  options: UseMockNotificationsOptions = {}
): UseMockNotificationsResult {
  const { limit = DEFAULT_PAGE_SIZE } = options;

  const [notifications, setNotifications] = useState<MockNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void getNotifications({
      offset: 0,
      limit,
    }).then((res) => {
      if (!active) return;
      setNotifications(res.notifications);
      setTotalCount(res.totalCount);
      setUnreadCount(res.unreadCount);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [limit]);

  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    void getNotifications({
      offset: notifications.length,
      limit,
    }).then((res) => {
      setNotifications((prev) => [...prev, ...res.notifications]);
      setTotalCount(res.totalCount);
      setUnreadCount(res.unreadCount);
      setLoadingMore(false);
    });
  }, [loadingMore, notifications.length, limit]);

  const markAsRead = useCallback((notificationId: number) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.notificationId === notificationId ? { ...n, isRead: true } : n
      )
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
    void markReadService({ notificationId });
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    void markAllReadService();
  }, []);

  const remove = useCallback((notificationId: number) => {
    setNotifications((prev) => {
      const target = prev.find((n) => n.notificationId === notificationId);
      if (target && !target.isRead) {
        setUnreadCount((u) => Math.max(0, u - 1));
      }
      return prev.filter((n) => n.notificationId !== notificationId);
    });
    setTotalCount((prev) => Math.max(0, prev - 1));
    void deleteService({ notificationId });
  }, []);

  return {
    notifications,
    unreadCount,
    totalCount,
    loading,
    loadingMore,
    hasMore: notifications.length < totalCount,
    loadMore,
    markAsRead,
    markAllAsRead,
    remove,
  };
}
